import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import AuthService from '../services/authService'
import { SimplePermissionService } from '../services/simplePermissionService'

// 认证状态管理
export const useAuthStore = create(
  persist(
    (set, get) => ({
      // 状态
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // 初始化认证状态
      initAuth: async () => {
        try {
          set({ isLoading: true, error: null })
          
          // 从认证服务获取当前用户
          const currentUser = AuthService.getCurrentUser()
          const isLoggedIn = AuthService.isLoggedIn()
          
          set({
            user: currentUser,
            isAuthenticated: isLoggedIn,
            isLoading: false
          })
          
          console.log('🔐 认证状态初始化完成:', isLoggedIn ? '已登录' : '未登录')
        } catch (error) {
          console.error('❌ 认证状态初始化失败:', error)
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: error.message
          })
        }
      },

      // 登录
      login: async (email, password) => {
        try {
          set({ isLoading: true, error: null })
          
          const result = await AuthService.login(email, password)
          
          if (result.success) {
            set({
              user: result.user,
              isAuthenticated: true,
              isLoading: false,
              error: null
            })
            return { success: true, user: result.user }
          } else {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: result.error
            })
            return { success: false, error: result.error }
          }
        } catch (error) {
          console.error('❌ 登录失败:', error)
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: error.message
          })
          return { success: false, error: error.message }
        }
      },

      // 注册
      register: async (userData) => {
        try {
          set({ isLoading: true, error: null })
          
          const result = await AuthService.register(userData)
          
          if (result.success) {
            set({
              user: result.user,
              isAuthenticated: true,
              isLoading: false,
              error: null
            })
            return { success: true, user: result.user }
          } else {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: result.error
            })
            return { success: false, error: result.error }
          }
        } catch (error) {
          console.error('❌ 注册失败:', error)
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: error.message
          })
          return { success: false, error: error.message }
        }
      },

      // 登出
      logout: async () => {
        try {
          set({ isLoading: true, error: null })
          
          const result = await AuthService.logout()
          
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null
          })
          
          return { success: true }
        } catch (error) {
          console.error('❌ 登出失败:', error)
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: error.message
          })
          return { success: false, error: error.message }
        }
      },

      // 更新用户资料
      updateProfile: async (updates) => {
        try {
          set({ isLoading: true, error: null })
          
          const result = await AuthService.updateProfile(updates)
          
          if (result.success) {
            set({
              user: result.user,
              isLoading: false,
              error: null
            })
            return { success: true, user: result.user }
          } else {
            set({
              isLoading: false,
              error: result.error
            })
            return { success: false, error: result.error }
          }
        } catch (error) {
          console.error('❌ 更新资料失败:', error)
          set({
            isLoading: false,
            error: error.message
          })
          return { success: false, error: error.message }
        }
      },

      // 修改密码
      changePassword: async (oldPassword, newPassword) => {
        try {
          set({ isLoading: true, error: null })
          
          const result = await AuthService.changePassword(oldPassword, newPassword)
          
          set({
            isLoading: false,
            error: result.success ? null : result.error
          })
          
          return result
        } catch (error) {
          console.error('❌ 修改密码失败:', error)
          set({
            isLoading: false,
            error: error.message
          })
          return { success: false, error: error.message }
        }
      },

      // 检查权限
      hasPermission: (permission) => {
        const { user } = get()
        return SimplePermissionService.hasPermission(permission, user)
      },

      // 检查角色
      hasRole: (role) => {
        const { user } = get()
        return SimplePermissionService.hasRole(role, user)
      },

      // 检查是否有任一权限
      hasAnyPermission: (permissions) => {
        const { user } = get()
        return permissions.some(permission => SimplePermissionService.hasPermission(permission, user))
      },

      // 检查是否有任一角色
      hasAnyRole: (roles) => {
        const { user } = get()
        return roles.includes(user?.role)
      },

      // 获取用户权限
      getUserPermissions: () => {
        const { user } = get()
        return SimplePermissionService.getUserPermissions(user)
      },

      // 获取用户角色信息
      getUserRoleInfo: () => {
        const { user } = get()
        if (!user) return null
        
        return {
          role: user.role,
          permissions: SimplePermissionService.getUserPermissions(user),
          isActive: user.isActive,
          canCreateStory: SimplePermissionService.hasPermission('story:create', user),
          canManageUsers: SimplePermissionService.hasPermission('user:manage', user),
          canModerateContent: SimplePermissionService.hasPermission('content:moderate', user),
          isAdmin: user.role === 'admin' || user.role === 'super_admin'
        }
      },

      // 检查是否可以访问资源
      canAccessResource: (resource, action, resourceOwner = null) => {
        const { user } = get()
        if (!user) return false
        
        const permission = `${resource}:${action}`
        return SimplePermissionService.hasPermission(permission, user)
      },

      // 检查是否可以修改资源
      canModifyResource: (resource, resourceOwner) => {
        const { user } = get()
        if (!user) return false
        
        // 如果是自己的资源，允许修改
        if (resourceOwner && resourceOwner.id === user.id) {
          return true
        }
        
        // 管理员可以修改所有资源
        return user.role === 'admin' || user.role === 'super_admin'
      },

      // 清除错误
      clearError: () => {
        set({ error: null })
      },

      // 设置加载状态
      setLoading: (loading) => {
        set({ isLoading: loading })
      },

      // 刷新用户数据
      refreshUser: async () => {
        try {
          const currentUser = AuthService.getCurrentUser()
          const isLoggedIn = AuthService.isLoggedIn()
          
          set({
            user: currentUser,
            isAuthenticated: isLoggedIn
          })
          
          return { success: true, user: currentUser }
        } catch (error) {
          console.error('❌ 刷新用户数据失败:', error)
          return { success: false, error: error.message }
        }
      }
    }),
    {
      name: 'altverse-auth-store',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
)

// 权限检查 Hook
export const usePermissions = () => {
  const {
    hasPermission,
    hasRole,
    hasAnyPermission,
    hasAnyRole,
    getUserPermissions,
    getUserRoleInfo,
    canAccessResource,
    canModifyResource
  } = useAuthStore()

  return {
    hasPermission,
    hasRole,
    hasAnyPermission,
    hasAnyRole,
    getUserPermissions,
    getUserRoleInfo,
    canAccessResource,
    canModifyResource
  }
}

// 认证状态 Hook
export const useAuth = () => {
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    initAuth,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    clearError,
    setLoading,
    refreshUser
  } = useAuthStore()

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    initAuth,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    clearError,
    setLoading,
    refreshUser
  }
}

export default useAuthStore
